// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useLocalStorage } from 'common/hooks';
import { createContext, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
    CopilotMessageFeedbackEnum,
    CopilotToolCallDecisionEnum,
    CopilotToolCallExecutionEnum,
} from 'schema';
import { toToolCalls } from './PageHeader/Copilot/ToolCalls/toolCalls';
import { addToolCallToast } from './PageHeader/Copilot/ToolCalls/toolCallToast';
import COPILOT_CONVERSATION_HISTORY from 'queries/CopilotConversationHistory.graphql';
import TOUCH_COPILOT_CONVERSATION from 'queries/TouchCopilotConversation.graphql';

const CURRENT_CONVERSATION_VARIABLES = { input: { archived: false, pagination: { page: 1, itemsPerPage: 1 } }, withMessages: true };

export type ToolCall = {
    // The wire tool_call_id. Wire ids such as call_0 repeat across turns, so a call is
    // identified by (messageId, id), never by id alone.
    id: string;
    // CopilotMessages.id of the assistant row that issued this batch, and the key both
    // tool-call mutations address a batch by
    messageId: string;
    type: string;
    // The qualified tool name
    name: string;
    // JSON-encoded string, passed through from the AI service verbatim
    arguments: string;
    // The tool's declared title, or null when it declares none - never the name fallback
    title: string | null;
    readOnly: boolean;
    // Persisted decision and execution state; the UI renders these and never re-derives
    // the approval policy. SKIPPED is written for a rejected call when its batch runs.
    decision: CopilotToolCallDecisionEnum | null;
    execution: CopilotToolCallExecutionEnum | null;
    // Display label, derived, truncated and snapshotted in PHP; the renderer only wraps it
    label: string;
    // Localized one-line summary of the result, or the error summary of a failed call,
    // derived from the full result before any size cap. The payload is fetched on demand.
    outcome: string | null;
    // Length of the full result and how much of it the model was replayed, in characters
    // as PHP counts them; null until the call has executed. Truncation is derived by
    // comparing them, never from a client-side copy of the cap.
    resultChars: number | null;
    modelResultChars: number | null;
    // How much of the result was stored for the admin; null until the call has executed
    storedChars: number | null;
};

export type Answer = {
    // Client-only handle, assigned once when the answer object is created and never
    // changed: the React key and what every client-side lookup uses
    key: string;
    // CopilotMessages.id once the turn is persisted, null before that (and forever for
    // an answer that is never persisted, e.g. an error); used only when talking to the server
    id: string | null;
    role: 'assistant' | 'user';
    content?: string;
    error?: string;
    feedback?: CopilotMessageFeedbackEnum | null;
    chat_id?: string;
    able_to_answer?: boolean;
    streaming?: boolean;
    toolCalls?: ToolCall[];
    // Localized refusal of the last run of this answer's batch; suppresses Run and the
    // automatic run until the next history reload, which rebuilds the answers
    runError?: string;
};

let localKeyCounter = 0;

// Key of an answer created in this session, before (or without) a persisted id
export const createLocalAnswerKey = (): string => `local-${localKeyCounter++}`;

// Key of an answer loaded from the conversation history
export const persistedAnswerKey = (id: string): string => `msg-${id}`;

type CopilotContextType = {
    isRightSidebarOpen: boolean;
    setIsRightSidebarOpen: (value: boolean) => void;
    loading: boolean;
    answers: Answer[];
    setAnswers: (value: Answer[] | ((prev: Answer[]) => Answer[])) => void;
    chatId: string | undefined;
    setChatId: (value: string | undefined) => void;
    openConversation: (chatId: string) => Promise<void>;
    // Reloads the conversation being shown from the server
    reloadConversation: () => Promise<void>;
    startNewChat: () => void;
    // Incremented by every history load that replaced the answers
    historyVersion: number;
    // Ids of the assistant rows whose batch is being run from this tab
    runInFlight: ReadonlySet<string>;
    // Registers a run of the batch; false, and nothing registered, when one is in flight
    beginRun: (messageId: string) => boolean;
    endRun: (messageId: string) => void;
};

export const CopilotContext = createContext<CopilotContextType>({
    isRightSidebarOpen: false,
    setIsRightSidebarOpen: () => { },
    loading: false,
    answers: [],
    setAnswers: () => { },
    chatId: undefined,
    setChatId: () => { },
    openConversation: () => Promise.resolve(),
    reloadConversation: () => Promise.resolve(),
    startNewChat: () => { },
    historyVersion: 0,
    runInFlight: new Set(),
    beginRun: () => true,
    endRun: () => { },
});

type CopilotContextProviderProps = {
    children: React.ReactNode;
    enabled?: boolean;
}

export const CopilotContextProvider = ({ children, enabled = true }: CopilotContextProviderProps) => {
    const [isRightSidebarOpen, setIsRightSidebarOpen] = useLocalStorage('isRightSidebarOpen', false);
    const [answers, setAnswers] = useState<Answer[]>([]);
    const [chatId, setChatId] = useState<string>();
    const [historyVersion, setHistoryVersion] = useState(0);

    // A ref alongside the state, so that two triggers in one render cannot both start a run
    const runInFlightRef = useRef<Set<string>>(new Set());
    const [runInFlight, setRunInFlight] = useState<ReadonlySet<string>>(() => new Set());
    const beginRun = useCallback((messageId: string) => {
        if (runInFlightRef.current.has(messageId)) {
            return false;
        }
        runInFlightRef.current = new Set(runInFlightRef.current).add(messageId);
        setRunInFlight(runInFlightRef.current);
        return true;
    }, []);
    const endRun = useCallback((messageId: string) => {
        const next = new Set(runInFlightRef.current);
        next.delete(messageId);
        runInFlightRef.current = next;
        setRunInFlight(next);
    }, []);

    const [isNewChat, setIsNewChat] = useLocalStorage('isCopilotNewChat', false);
    const isNewChatRef = useRef(isNewChat);
    isNewChatRef.current = isNewChat;

    const [loadConversationQuery, { loading, error: loadError }] = useLazyQuery(
        COPILOT_CONVERSATION_HISTORY,
        { fetchPolicy: 'network-only' },
    );
    const [touchConversation] = useMutation(TOUCH_COPILOT_CONVERSATION);

    // History replaces the answers only as the result of an explicit load, never on a later
    // cache update of the watched query (e.g. a SendCopilotFeedback result rewriting a
    // CopilotMessage), which would wipe the session's turns and tool call results. A newer
    // load or a new chat supersedes a load still in flight.
    const loadTokenRef = useRef(0);
    const loadConversation = useCallback(async (variables: typeof CURRENT_CONVERSATION_VARIABLES | {
        input: { externalId: string };
        withMessages: boolean;
    }) => {
        const token = ++loadTokenRef.current;
        let result;
        try {
            result = await loadConversationQuery({ variables });
        } catch {
            // Reported through loadError
            return;
        }
        const conversation = result?.data?.viewer?.copilotConversationHistory?.nodes?.[0];
        if (token !== loadTokenRef.current || !conversation?.messages) {
            return;
        }
        setChatId(conversation.externalId);
        // The keys are derived from the ids, so a run still in flight finds its answer again
        setAnswers(
            conversation.messages.map(message => ({
                key: persistedAnswerKey(message.id),
                id: message.id,
                role: message.role,
                content: message.content ?? undefined,
                feedback: message.feedback,
                toolCalls: toToolCalls(message.toolCalls),
                chat_id: conversation.externalId,
            })),
        );
        setHistoryVersion(version => version + 1);
    }, [loadConversationQuery]);

    const openConversation = useCallback(async (targetChatId: string) => {
        setIsRightSidebarOpen(true);
        await Promise.all([
            touchConversation({ variables: { input: { chatId: targetChatId } } }),
            loadConversation({ input: { externalId: targetChatId }, withMessages: true }),
        ]);
    }, [loadConversation, touchConversation, setIsRightSidebarOpen]);

    const chatIdRef = useRef(chatId);
    // A layout effect: Chat's effects, which run before this provider's own, call reloadConversation()
    useLayoutEffect(() => {
        chatIdRef.current = chatId;
    });
    const reloadConversation = useCallback(async () => {
        const currentChatId = chatIdRef.current;
        await loadConversation(currentChatId
            ? { input: { externalId: currentChatId }, withMessages: true }
            : CURRENT_CONVERSATION_VARIABLES);
    }, [loadConversation]);

    useEffect(() => {
        if (loadError) {
            // Also a background reload's, e.g. of a recheck of a run: repeated ones must not
            // stack up over the header
            addToolCallToast('danger', String(loadError));
        }
    }, [loadError]);

    useEffect(() => {
        if (chatId) {
            setIsNewChat(false);
        }
    }, [chatId, setIsNewChat]);

    useEffect(() => {
        if (enabled && isRightSidebarOpen && !isNewChatRef.current) {
            loadConversation(CURRENT_CONVERSATION_VARIABLES);
        }
    }, [enabled, isRightSidebarOpen, loadConversation]);

    const startNewChat = useCallback(() => {
        loadTokenRef.current++;
        setAnswers([]);
        setChatId(undefined);
        setIsNewChat(true);
    }, [setIsNewChat]);

    const contextValue = useMemo(() => ({
        isRightSidebarOpen,
        setIsRightSidebarOpen,
        loading,
        answers,
        setAnswers,
        chatId,
        setChatId,
        openConversation,
        reloadConversation,
        startNewChat,
        historyVersion,
        runInFlight,
        beginRun,
        endRun,
    }), [
        isRightSidebarOpen, setIsRightSidebarOpen, loading, answers, setAnswers, chatId, openConversation, reloadConversation,
        startNewChat, historyVersion, runInFlight, beginRun, endRun,
    ]);

    return (
        <CopilotContext.Provider value={contextValue}>
            {children}
        </CopilotContext.Provider>
    );
};
